import React, { useState, useEffect,  useRef } from 'react';
import { Box, CssBaseline, Typography } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Routes, Route, Navigate, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { db } from '../firebase';
import { collection, query, where, onSnapshot, doc, getDoc, updateDoc, getDocs, addDoc } from 'firebase/firestore';
import { getAuth, signOut } from 'firebase/auth';
import { v4 as uuidv4 } from 'uuid';
import Navbar from '../components/Navbar';
import Sidebar from '../components/Sidebar';
import Footer from '../components/Footer';
import AdminRoutes from '../routes/AdminRoutes';
import CustomerRoutes from '../routes/CustomerRoutes';
import KitchenRoutes from '../routes/KitchenRoutes';
import ServerRoutes from '../routes/ServerRoutes';
import MasterRoutes from '../routes/MasterRoutes';
import UnitManagerRoutes from '../routes/ManagerRoutes';
import ViewMenu from '../pages/customer/ViewMenu';
import PlaceOrder from '../pages/customer/PlaceOrder';
import TrackOrder from '../pages/customer/TrackOrder';
import RequestBill from '../pages/customer/RequestBill';
import Cart from '../pages/customer/Cart';
import LandingPage from '../pages/LandingPage';

import { menuService } from '../services/menuService';


const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

const drawerWidth = 300;

function RoleLayout({ darkMode, toggleDarkMode, role = '', userId, onLoginSuccess }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const isMobileOrLaptop = useMediaQuery(theme.breakpoints.down('lg'));
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [open, setOpen] = useState(false);
  const [openCartDrawer, setOpenCartDrawer] = useState(false);
  const [cart, setCart] = useState(() => {
    const savedCart = localStorage.getItem('cart');
    return savedCart ? JSON.parse(savedCart) : [];
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [cartMarginAdjustment, setCartMarginAdjustment] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [assignedTableIds, setAssignedTableIds] = useState([]);
  const [tableId, setTableId] = useState(searchParams.get('id') || sessionStorage.getItem(`tab_${location.key}_tableId`) || null);
  const [companyCode, setCompanyCode] = useState(
    searchParams.get('code') || 
    location.state?.companyCode || 
    sessionStorage.getItem(`tab_${location.key}_companyCode`) || 
    sessionStorage.getItem('companyCode') || 
    null
  );
  const [unitCode, setUnitCode] = useState(
    searchParams.get('unit') || 
    location.state?.unitCode || 
    sessionStorage.getItem(`tab_${location.key}_unitCode`) || 
    sessionStorage.getItem('unitCode') || 
    (userId && userId.includes('#') ? userId.split('#')[0] : null)
  );
  const [companyName, setCompanyName] = useState(
    searchParams.get('name') ? decodeURIComponent(searchParams.get('name')) : 
    location.state?.companyName || 
    sessionStorage.getItem(`tab_${location.key}_companyName`) || 
    sessionStorage.getItem('companyName') || 
    null
  );
  const [unitType, setUnitType] = useState(
    searchParams.get('type') || 
    location.state?.unitType || 
    sessionStorage.getItem(`tab_${location.key}_unitType`) || 
    sessionStorage.getItem('unitType') || 
    null
  );
  const [isPureVeg, setIsPureVeg] = useState(
    searchParams.get('isPureVeg') ? searchParams.get('isPureVeg') === 'true' : 
    location.state?.isPureVeg ?? 
    (sessionStorage.getItem(`tab_${location.key}_isPureVeg`) === 'true' || 
     sessionStorage.getItem('isPureVeg') === 'true') ?? 
    null
  );
  const [error, setError] = useState(null);
  const auth = getAuth();
 /* const [cartId, setCartId] = useState(() => {
  return sessionStorage.getItem("cartId") || null;
});*/
/*const [cartId, setCartId] = useState(() => {
  return (
    location.state?.existingCart?.cartId ||
    sessionStorage.getItem("cartId") ||
    null
  );
});*/

const [billingPreference, setBillingPreference] = useState(null);
const [billingLoading, setBillingLoading] = useState(true);

const [profilePreference, setProfilePreference] = useState(null);
const [profileLoading, setProfileLoading] = useState(true);

const skipCartFlow =
  role?.toLowerCase() === "customer" &&
  !billingLoading &&
  (billingPreference === 0 || billingPreference === 1);

const [cartId, setCartId] = useState(() => {
  if (role?.toLowerCase() === "customer" && skipCartFlow) return null;

  return (
    location.state?.existingCart?.cartId ||
    sessionStorage.getItem("cartId") ||
    null
  );
});
const [serviceArea, setServiceArea] = useState(
  searchParams.get('serviceArea') ||
  location.state?.serviceArea ||
  sessionStorage.getItem(`tab_${location.key}_serviceArea`) ||
  sessionStorage.getItem('serviceArea') ||
  null
);

useEffect(() => {
  const fetchBillingPreference = async () => {
    if (!companyCode || !unitCode) return;

    try {
      setBillingLoading(true);

      const q = query(
        collection(db, "preferences"),
        where("companyCode", "==", companyCode),
        where("unitCode", "==", unitCode),
        where("preferenceName", "==", "Billing Process")
      );

      const snapshot = await getDocs(q);

      if (!snapshot.empty) {
        const data = snapshot.docs[0].data();
        setBillingPreference(Number(data.preferenceValue));
        console.log("Billing Preference:", data.preferenceValue);
      } else {
        setBillingPreference(null);
      }

    } catch (error) {
      console.error("Billing preference error:", error);
      setBillingPreference(null);
    } finally {
      setBillingLoading(false);
    }
  };

  fetchBillingPreference();
}, [companyCode, unitCode]);


useEffect(() => {
  const fetchProfilePreference = async () => {
    if (!companyCode || !unitCode) return;

    setProfileLoading(true);

    const q = query(
      collection(db, "preferences"),
      where("companyCode", "==", companyCode),
      where("unitCode", "==", unitCode),
      where("preferenceName", "==", "Profile Data")
    );

    const snapshot = await getDocs(q);

    if (!snapshot.empty) {
      setProfilePreference(Number(snapshot.docs[0].data().preferenceValue));
    } else {
      setProfilePreference(0);
    }

    setProfileLoading(false);
  };

  fetchProfilePreference();
}, [companyCode, unitCode]);


useEffect(() => {
  const fetchServiceArea = async () => {
    if (!companyCode || !unitCode || !tableId) return;

    try {
      const docId = `${companyCode}_${unitCode}_${tableId}`;
      const tableDoc = await getDoc(doc(db, "tables", docId));

      if (tableDoc.exists()) {
        const data = tableDoc.data();
        const service = data.serviceArea || null;

        setServiceArea(service);
        sessionStorage.setItem("serviceArea", service);

        console.log("Fetched serviceArea:", service);
      }
    } catch (err) {
      console.error("Error fetching serviceArea:", err);
    }
  };

  fetchServiceArea();
}, [companyCode, unitCode, tableId]);



useEffect(() => {
  if (serviceArea) {
    sessionStorage.setItem('serviceArea', serviceArea);
    sessionStorage.setItem(`tab_${location.key}_serviceArea`, serviceArea);
  }
}, [serviceArea, location.key]);
//const incomingCart = location.state?.existingCart || null;

const incomingCart = (() => {
   if (skipCartFlow) return null;
  // 1. Fresh navigation (QR scan / redirect)
  if (location.state?.existingCart) {
    sessionStorage.setItem(
      "existingCart",
      JSON.stringify(location.state.existingCart)
    );
    return location.state.existingCart;
  }

  // 2. Fallback (refresh / production)
  const stored = sessionStorage.getItem("existingCart");
  return stored ? JSON.parse(stored) : null;
})();

const forceNewCart =
  location.state?.forceNewCart ||
  sessionStorage.getItem("forceNewCart") === "true";

useEffect(() => {
  if (location.state?.forceNewCart) {
    sessionStorage.setItem("forceNewCart", "true");
  }
}, [location.state]);

useEffect(() => {
  if (forceNewCart) return;

  if (incomingCart?.cartId) {
    console.log("Using scanned cartId:", incomingCart.cartId);

    setCartId(incomingCart.cartId);
    sessionStorage.setItem("cartId", incomingCart.cartId);
  }
}, [incomingCart, forceNewCart]);
/*useEffect(() => {
  if (location.state?.forceNewCart) return; 

  if (incomingCart?.cartId) {
    console.log("🔥 Using scanned cartId:", incomingCart.cartId);

    setCartId(incomingCart.cartId);
    sessionStorage.setItem("cartId", incomingCart.cartId);
  }
}, [incomingCart, location.state]);*/

/*useEffect(() => {
  if (location.state?.forceNewCart) {
    console.log("🔥 Forcing new cart creation");

    setCartId(null);
    sessionStorage.removeItem("cartId");

    hasCreatedCart.current = false;
  }
}, [location.state]);*/

/*useEffect(() => {
  if (forceNewCart) {
    console.log("Forcing new cart creation");

    setCartId(null);

    sessionStorage.removeItem("cartId");
    sessionStorage.removeItem("existingCart");
    sessionStorage.removeItem("forceNewCart");

    hasCreatedCart.current = false;
  }
}, [forceNewCart]);*/
useEffect(() => {
  if (forceNewCart) {
    console.log("Forcing new cart creation");

    //  CRITICAL FIX
    setCart([]);                     // clear UI cart
    localStorage.removeItem("cart"); // clear persisted cart

    setCartId(null);

    sessionStorage.removeItem("cartId");
    sessionStorage.removeItem("existingCart");
    sessionStorage.removeItem("forceNewCart");

    hasCreatedCart.current = false;
  }
}, [forceNewCart]);

const hasCreatedCart = useRef(false);

// ✅ Move function OUTSIDE
const createOrFetchCart = async () => {
  if (skipCartFlow) return;
  if (!companyCode || !unitCode || !tableId || !serviceArea) return;

  try {

     if (forceNewCart) {
      const newCartId = Math.floor(1000 + Math.random() * 9000).toString();

      const newCart = {
        cartId: newCartId,
        companyCode,
        unitCode,
        tableId,
        serviceArea,
        cartStatus: "Empty",
        items: [],
        createdAt: new Date()
      };

      const docRef = await addDoc(collection(db, "carts"), newCart);
      await updateDoc(docRef, { id: docRef.id });

      setCartId(newCartId);
      sessionStorage.setItem("cartId", newCartId);

      console.log("🔥 Forced new cart created", newCart);
      return;
    }
    const cartQuery = query(
      collection(db, "carts"),
      where("companyCode", "==", companyCode),
      where("unitCode", "==", unitCode),
      where("tableId", "==", tableId),
      where("serviceArea", "==", serviceArea)
    );

    const snapshot = await getDocs(cartQuery);

    if (!snapshot.empty) {
      const existingCart = snapshot.docs[0].data();

      setCartId(existingCart.cartId);
      sessionStorage.setItem("cartId", existingCart.cartId);

      console.log("RoleLayout: Existing cart", existingCart);
    } else {
      const newCartId = Math.floor(1000 + Math.random() * 9000).toString();

      const newCart = {
        cartId: newCartId,
        companyCode,
        unitCode,
        tableId,
        serviceArea,
        cartStatus: "Empty",
        items: [],
        createdAt: new Date()
      };

      const docRef = await addDoc(collection(db, "carts"), newCart);
      await updateDoc(docRef, { id: docRef.id });

      setCartId(newCartId);
      sessionStorage.setItem("cartId", newCartId);

      console.log("RoleLayout: New cart created", newCart);
    }
  } catch (err) {
    console.error("RoleLayout cart error:", err);
  }
};


useEffect(() => {
    if (skipCartFlow) return;   // 🚨 HARD STOP
  if (billingLoading) return;
  if (hasCreatedCart.current) return;

  if (!companyCode || !unitCode || !tableId || !serviceArea) return;

  // ✅ Only skip if NOT forcing new cart
  if (cartId && !forceNewCart) {
    console.log("Using existing cartId:", cartId);
    hasCreatedCart.current = true;
    return;
  }

  hasCreatedCart.current = true;

  createOrFetchCart();
}, [ skipCartFlow,
  billingLoading, companyCode, unitCode, tableId, serviceArea, cartId, location.state]);

useEffect(() => { 
  if (billingLoading) return; 

  const isReady = companyCode && unitCode && tableId && serviceArea;
  if (!skipCartFlow) return; 

  if (!isReady) { 
    console.log("Waiting for context before redirect..."); 
    return; 
  } 

  // ✅ ONLY block cart page
  if (location.pathname.includes("/customer/cart")) {
    console.log("Skipping cart page → redirecting to home");

    navigate(
      `/customer/home?code=${companyCode}&unit=${unitCode}&id=${tableId}&serviceArea=${serviceArea || ""}`,
      { replace: true }
    );
  }

}, [
  skipCartFlow,
  billingLoading,
  companyCode,
  unitCode,
  tableId,
  serviceArea,
  location.pathname   // ✅ IMPORTANT
]);

useEffect(() => {
  if (profileLoading || billingLoading) return;
  if (role?.toLowerCase() !== "customer") return;

  const isGuest = profilePreference === 1;
  if (!isGuest) return;

  if (!cartId) return;

  // CASE 1: cart already exists → join cart
   if (location.state?.existingCart) {
    navigate(
      `/customer/join-cart?code=${companyCode}&unit=${unitCode}&id=${tableId}&serviceArea=${serviceArea}`,
      {
        state: {
          companyCode,
          unitCode,
          tableId,
          serviceArea,
          existingCart: location.state.existingCart,
          isCustomer: false,
          autoJoin: true,
        },
        replace: true,
      }
    );
    return;
  }

  // CASE 2: no existing cart → go to guest home
  navigate(
    `/customer/home?code=${companyCode}&unit=${unitCode}&id=${tableId}&serviceArea=${serviceArea}&isCustomer=false&name=Guest`,
    {
      state: {
        companyCode,
        unitCode,
        tableId,
        serviceArea,
        isCustomer: false,
        customerName: "Guest",
        phone: null,
        autoGuest: true,
      },
      replace: true,
    }
  );
}, [profilePreference, profileLoading, billingLoading, cartId]);


const getCartStatus = (items) => {
  if (!items.length) return "Empty";

  const statuses = items.map(i => i.status);

  // ✅ If ANY item is Served → cart stays Served
  if (statuses.includes("Served")) return "Served";

  // Full matches
  if (statuses.every(s => s === "Ready")) return "Ready";
  if (statuses.every(s => s === "Preparing")) return "Preparing";
  if (statuses.every(s => s === "Confirmed")) return "Confirmed";

  // Mixed cases → pick highest (correct order!)
  if (statuses.includes("Ready")) return "Ready";
  if (statuses.includes("Preparing")) return "Preparing";
  if (statuses.includes("Confirmed")) return "Confirmed";
  if (statuses.includes("Ordered")) return "Ordered";

  return "Added";
};
const updateCartInFirestore = async (updatedCart, extraData = {}) => {
  if (skipCartFlow) return;
  try {
    if (!cartId) return;

    const cartQuery = query(
      collection(db, "carts"),
      where("cartId", "==", cartId),
      where("companyCode", "==", companyCode),
      where("unitCode", "==", unitCode),
      where("tableId", "==", tableId),
      where("serviceArea", "==", serviceArea)
    );

    const snapshot = await getDocs(cartQuery);

    if (snapshot.empty) {
      console.warn("❌ No cart found");
      return;
    }

    const cartDoc = snapshot.docs[0];
    const existingData = cartDoc.data();

    const existingItems = existingData.items || [];

    // Separate ordered items
    /*const orderedItems = existingItems.filter(
      item => item.status === "Ordered"
    );*/
    const lockedItems = existingItems.filter(
  item => ["Ordered", "Confirmed", "Preparing", "Ready", "Served"].includes(item.status)
);

    // New/updated items (from UI)
    const newItems = updatedCart.map(item => ({
      id: item.id,
      itemInstanceId:
        item.itemInstanceId || `${item.id}_${Date.now()}_${Math.random()}`,
      imageUrl: item.imageUrl || "",
      itemCode: item.itemCode || "",
      name: item.name,
      price: item.price,
      quantity: item.quantity,
      status: item.status || "Added",
      totalPrice: item.totalPrice
    }));

    //  Merge: keep old ordered + new added
   // const finalItems = [...lockedItems, ...newItems];
   const nonLockedItems = existingItems.filter(
  item => !["Ordered", "Confirmed", "Preparing", "Ready", "Served"].includes(item.status)
);

//  Replace only non-locked items
const finalItems = [
  ...lockedItems,
  ...newItems.map(newItem => {
    // try match existing non-locked item
    const existing = nonLockedItems.find(
      old => old.itemInstanceId === newItem.itemInstanceId
    );

    return existing ? { ...existing, ...newItem } : newItem;
  })
];

const computedCartStatus = getCartStatus(finalItems);
    await updateDoc(cartDoc.ref, {
      items: finalItems,

      // DO NOT downgrade cartStatus
      /*cartStatus:
        existingData.cartStatus === "Ordered"
          ? "Ordered"
          : (extraData.cartStatus || (newItems.length > 0 ? "Added" : "Empty")),*/
      cartStatus: computedCartStatus,
      //assignedServerId: extraData.assignedServerId || null,
      assignedServerId:
  extraData.assignedServerId ?? existingData.assignedServerId ?? null,
      updatedAt: new Date()
    });

    console.log("Firestore Updated:", finalItems);

  } catch (error) {
    console.error("Error updating cart:", error);
  }
};

  useEffect(() => {
    console.log('RoleLayout: Initial state:', {
      role,
      userId,
      companyCode,
      unitCode,
      unitType,
      companyName,
      isPureVeg,
      tableId,
      searchParams: Object.fromEntries(searchParams),
      locationState: location.state,
      tabKey: location.key,
    });


    const fetchCompanyAndUnitData = async () => {
      let code = searchParams.get('code') || location.state?.companyCode || sessionStorage.getItem(`tab_${location.key}_companyCode`) || sessionStorage.getItem('companyCode');
      let extractedUnitCode = searchParams.get('unit') || location.state?.unitCode || sessionStorage.getItem(`tab_${location.key}_unitCode`) || sessionStorage.getItem('unitCode');
      let urlUnitType = searchParams.get('type') || location.state?.unitType || sessionStorage.getItem(`tab_${location.key}_unitType`) || sessionStorage.getItem('unitType');
      let urlCompanyName = searchParams.get('name') ? decodeURIComponent(searchParams.get('name')) : location.state?.companyName || sessionStorage.getItem(`tab_${location.key}_companyName`) || sessionStorage.getItem('companyName');
      let urlIsPureVeg = searchParams.get('isPureVeg') ? searchParams.get('isPureVeg') === 'true' : location.state?.isPureVeg ?? (sessionStorage.getItem(`tab_${location.key}_isPureVeg`) === 'true' || sessionStorage.getItem('isPureVeg') === 'true');

      // Extract unitCode from userId for unit-based roles if not provided
      if (!extractedUnitCode && role.toLowerCase() !== 'customer' && userId && ['unit_manager', 'kitchen_staff', 'servers'].includes(role.toLowerCase())) {
        const parts = userId.split('#');
        if (parts.length === 2 && parts[0]) {
          extractedUnitCode = parts[0];
        }
      }

      // Extract companyCode from userId for admin if not provided
      if (!code && role.toLowerCase() === 'admin' && userId) {
        const parts = userId.split('#');
        if (parts.length === 2 && parts[0]) {
          code = parts[0];
        }
      }

      // Validate unitCode for unit_manager, kitchen_staff, servers, or customer with unitCode
      if (extractedUnitCode) {
        try {
          const unitDocRef = doc(db, 'units', extractedUnitCode);
          const unitDoc = await getDoc(unitDocRef);
          if (unitDoc.exists()) {
            const unitData = unitDoc.data();
            setUnitCode(extractedUnitCode);
            setUnitType(unitData.unitType || 'Unknown');
            setIsPureVeg(unitData.isPureVeg || false);
            setCompanyName(unitData.companyName || 'Unknown Company');
            code = unitData.companyCode || code;
            setCompanyCode(code);

            // Store in sessionStorage (non-tab-specific for consistency)
            sessionStorage.setItem('unitCode', extractedUnitCode);
            sessionStorage.setItem('companyCode', code);
            sessionStorage.setItem('unitType', unitData.unitType || 'Unknown');
            sessionStorage.setItem('companyName', unitData.companyName || 'Unknown Company');
            sessionStorage.setItem('isPureVeg', unitData.isPureVeg ? 'true' : 'false');
            sessionStorage.setItem(`tab_${location.key}_unitCode`, extractedUnitCode);
            sessionStorage.setItem(`tab_${location.key}_companyCode`, code);
            sessionStorage.setItem(`tab_${location.key}_unitType`, unitData.unitType || 'Unknown');
            sessionStorage.setItem(`tab_${location.key}_companyName`, unitData.companyName || 'Unknown Company');
            sessionStorage.setItem(`tab_${location.key}_isPureVeg`, unitData.isPureVeg ? 'true' : 'false');

            // Validate URL unitType
            // if (urlUnitType && urlUnitType !== unitData.unitType) {
            //   console.warn('RoleLayout: URL unitType mismatch:', { urlUnitType, firestoreUnitType: unitData.unitType, unitCode: extractedUnitCode });
            //   setError(`Unit type mismatch. URL type (${urlUnitType}) does not match Firestore type (${unitData.unitType}).`);
            //   if (role.toLowerCase() !== 'customer') {
            //     navigate('/employee-login');
            //     return;
            //   }
            // }

            console.log('RoleLayout: Unit data fetched:', {
              unitCode: extractedUnitCode,
              companyCode: code,
              unitType: unitData.unitType,
              companyName: unitData.companyName,
              isPureVeg: unitData.isPureVeg,
              tabKey: location.key,
            });
          } else {
            console.error('RoleLayout: Unit not found for code:', extractedUnitCode);
            setError('Unit not found. Please check the unit code.');
            if (role.toLowerCase() !== 'customer') {
              navigate('/employee-login');
            }
            return;
          }
        } catch (error) {
          console.error('RoleLayout: Error fetching unit data:', { error, unitCode: extractedUnitCode, role });
          setError(`Failed to fetch unit data: ${error.message}`);
          if (role.toLowerCase() !== 'customer') {
            navigate('/employee-login');
          }
          return;
        }
      }

      // Validate companyCode for admin
      if (code && role.toLowerCase() === 'admin') {
        try {
          const companyDocRef = doc(db, 'companies', code);
          const companyDoc = await getDoc(companyDocRef);
          if (companyDoc.exists()) {
            const data = companyDoc.data();
            setCompanyCode(code);
            setCompanyName(data.name || 'Unknown Company');
            setIsPureVeg(data.isPureVeg || false);
            setUnitType(null); // Admin doesn't use unitType
            sessionStorage.setItem('companyCode', code);
            sessionStorage.setItem('companyName', data.name || 'Unknown Company');
            sessionStorage.setItem('isPureVeg', data.isPureVeg ? 'true' : 'false');
            sessionStorage.setItem(`tab_${location.key}_companyCode`, code);
            sessionStorage.setItem(`tab_${location.key}_companyName`, data.name || 'Unknown Company');
            sessionStorage.setItem(`tab_${location.key}_isPureVeg`, data.isPureVeg ? 'true' : 'false');
            sessionStorage.removeItem(`tab_${location.key}_unitType`);
            sessionStorage.removeItem('unitType');
            console.log('RoleLayout: Company data fetched:', {
              companyCode: code,
              companyName: data.name,
              isPureVeg: data.isPureVeg,
              tabKey: location.key,
            });
          } else {
            console.error('RoleLayout: Company not found for code:', code);
            setError('Company not found. Please check the company code.');
            navigate('/employee-login');
            return;
          }
        } catch (error) {
          console.error('RoleLayout: Error fetching company data:', { error, companyCode: code, role });
          setError(`Failed to fetch company data: ${error.message}`);
          navigate('/employee-login');
          return;
        }
      } else if (role.toLowerCase() !== 'softwareadmin' && role.toLowerCase() !== 'customer' && !extractedUnitCode && !code) {
        console.warn('RoleLayout: No company code or unit code available, redirecting to login');
        setError('No company code or unit code provided. Please log in.');
        navigate('/employee-login');
        return;
      }
    };

    fetchCompanyAndUnitData();
  }, [searchParams, userId, role, navigate, location.key, location.state]);

  useEffect(() => {
    const newTableId = searchParams.get('id') || sessionStorage.getItem(`tab_${location.key}_tableId`);
    console.log('RoleLayout: searchParams:', Object.fromEntries(searchParams));
    console.log('RoleLayout: tableId extracted:', { newTableId, currentTableId: tableId, role, unitCode, unitType, companyCode, tabKey: location.key });

    if (role.toLowerCase() !== 'customer') {
      setTableId(newTableId);
      if (newTableId) {
        sessionStorage.setItem(`tab_${location.key}_tableId`, newTableId);
        sessionStorage.setItem('tableId', newTableId);
      }
      return;
    }

    const manageSession = async () => {
      const oldDocId = tableId && companyCode && unitCode ? `${companyCode}_${unitCode}_${tableId}` : null;
      const newDocId = newTableId && companyCode && unitCode ? `${companyCode}_${unitCode}_${newTableId}` : null;

      if (newTableId && companyCode && unitCode) {
        try {
          const tableDoc = await getDoc(doc(db, 'tables', newDocId));
          if (!tableDoc.exists()) {
            console.error('RoleLayout: Table not found:', { newDocId, companyCode, unitCode, unitType });
            setError(`Table ${newTableId} does not exist for company ${companyCode} and unit ${unitCode}.`);
            return;
          }

          const tableData = tableDoc.data();
          if (tableData.unitType !== unitType) {
            console.warn('RoleLayout: Table unitType mismatch:', { tableUnitType: tableData.unitType, currentUnitType: unitType, newDocId });
            setError(`Table unit type (${tableData.unitType}) does not match expected unit type (${unitType}).`);
            return;
          }

          const sessionId = sessionStorage.getItem(`tab_${location.key}_sessionId`) || uuidv4();
          sessionStorage.setItem(`tab_${location.key}_sessionId`, sessionId);
          sessionStorage.setItem(`tab_${location.key}_tableId`, newTableId);
          sessionStorage.setItem(`tab_${location.key}_companyCode`, companyCode);
          sessionStorage.setItem(`tab_${location.key}_unitCode`, unitCode);
          sessionStorage.setItem(`tab_${location.key}_unitType`, unitType || 'Unknown');
          sessionStorage.setItem('tableId', newTableId);
          sessionStorage.setItem('companyCode', companyCode);
          sessionStorage.setItem('unitCode', unitCode);
          sessionStorage.setItem('unitType', unitType || 'Unknown');
          sessionStorage.setItem('companyName', companyName || 'Unknown Company');
          sessionStorage.setItem('isPureVeg', isPureVeg ? 'true' : 'false');

          const activeSessions = Array.isArray(tableData.activeSessions) ? tableData.activeSessions : [];
          const existingSession = activeSessions.find(session => session.sessionId === sessionId);
          const updatedSessions = existingSession
            ? activeSessions.map(session =>
                session.sessionId === sessionId ? { ...session, lastSeen: new Date() } : session
              )
            : [...activeSessions, { sessionId, lastSeen: new Date() }];

          await updateDoc(doc(db, 'tables', newDocId), {
            activeSessions: updatedSessions,
            companyCode,
            unitCode,
            tableId: newTableId,
            unitType,
            occupied: true,
          }, { merge: true });
          console.log('RoleLayout: Updated session for table:', { newDocId, sessionId, unitCode, unitType, companyCode, activeSessions: updatedSessions, tabKey: location.key });
        } catch (error) {
          console.error('RoleLayout: Error updating session:', { newDocId, error, unitCode, unitType, companyCode });
          setError(`Failed to update table session: ${error.message}`);
        }
      }

      if (oldDocId && newTableId !== tableId && companyCode && unitCode) {
        try {
          const tableDoc = await getDoc(doc(db, 'tables', oldDocId));
          if (tableDoc.exists()) {
            const tableData = tableDoc.data();
            const activeSessions = Array.isArray(tableData.activeSessions) ? tableData.activeSessions : [];
            const sessionId = sessionStorage.getItem(`tab_${location.key}_sessionId`);
            const updatedSessions = activeSessions.filter(session => session.sessionId !== sessionId);
            await updateDoc(doc(db, 'tables', oldDocId), { activeSessions: updatedSessions });
            console.log('RoleLayout: Removed session from previous table:', { oldDocId, sessionId, unitCode, unitType, companyCode, activeSessions: updatedSessions, tabKey: location.key });
          }
        } catch (error) {
          console.error('RoleLayout: Error removing session from previous table:', { oldDocId, error, unitCode, unitType, companyCode });
          setError(`Failed to reset table session: ${error.message}`);
        }
      }

      setTableId(newTableId);
    };

    if (role.toLowerCase() === 'customer') {
      manageSession();
    }
  }, [searchParams, tableId, role, companyCode, unitCode, unitType, location.key]);

  useEffect(() => {
    if (role.toLowerCase() === 'customer' && tableId && companyCode && unitCode && unitType) {
      const sessionId = sessionStorage.getItem(`tab_${location.key}_sessionId`);
      if (!sessionId) return;

      const interval = setInterval(async () => {
        try {
          const docId = `${companyCode}_${unitCode}_${tableId}`;
          const tableDoc = await getDoc(doc(db, 'tables', docId));
          if (tableDoc.exists()) {
            const tableData = tableDoc.data();
            const activeSessions = Array.isArray(tableData.activeSessions) ? tableData.activeSessions : [];
            const updatedSessions = activeSessions.map(session =>
              session.sessionId === sessionId ? { ...session, lastSeen: new Date() } : session
            );
            await updateDoc(doc(db, 'tables', docId), { activeSessions: updatedSessions });
            console.log('RoleLayout: Updated lastSeen for session:', { docId, sessionId, unitCode, unitType, companyCode, tabKey: location.key });
          }
        } catch (error) {
          console.error('RoleLayout: Error updating lastSeen:', { tableId, unitCode, unitType, companyCode, error });
        }
      }, 5000);

      const handleBeforeUnload = async () => {
        try {
          const docId = `${companyCode}_${unitCode}_${tableId}`;
          const tableDoc = await getDoc(doc(db, 'tables', docId));
          if (tableDoc.exists()) {
            const tableData = tableDoc.data();
            const activeSessions = Array.isArray(tableData.activeSessions) ? tableData.activeSessions : [];
            const updatedSessions = activeSessions.filter(session => session.sessionId !== sessionId);
            await updateDoc(doc(db, 'tables', docId), { activeSessions: updatedSessions });
            console.log('RoleLayout: Removed session on tab close:', { docId, sessionId, unitCode, unitType, companyCode, tabKey: location.key });
          }
        } catch (error) {
          console.error('RoleLayout: Error removing session on tab close:', { tableId, unitCode, unitType, companyCode, error });
        }
      };

      window.addEventListener('beforeunload', handleBeforeUnload);

      return () => {
        clearInterval(interval);
        window.removeEventListener('beforeunload', handleBeforeUnload);
        handleBeforeUnload();
      };
    }
  }, [tableId, role, companyCode, unitCode, unitType, location.key]);

  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(cart));
    console.log('RoleLayout: Cart updated and saved to localStorage:', cart);
  }, [cart]);

  useEffect(() => {
    const fetchAssignedTables = async () => {
      if (!unitCode || !userId) {
        console.warn('RoleLayout: No unitCode or userId for fetching assigned tables, skipping');
        return [];
      }
      try {
        const tablesQuery = query(
          collection(db, 'tables'),
          where('unitCode', '==', unitCode),
          where('assignedServer', '==', userId)
        );
        const tablesSnapshot = await getDocs(tablesQuery);
        const tableIds = tablesSnapshot.docs.map(doc => doc.data().tableId);
        console.log('RoleLayout: Fetched assigned tables:', { userId, tableIds, unitCode, unitType, companyCode, tabKey: location.key });
        return tableIds;
      } catch (error) {
        console.error('RoleLayout: Error fetching assigned tables:', { userId, unitCode, unitType, companyCode, error });
        setError(`Failed to fetch assigned tables: ${error.message}`);
        return [];
      }
    };

    const roleLower = role?.toLowerCase();
    if (!unitCode || !userId) {
      console.warn('RoleLayout: Missing unitCode or userId for notifications, skipping fetch');
      setNotifications([]);
      return;
    }

    if (roleLower === 'servers') {
      fetchAssignedTables().then(tableIds => {
        setAssignedTableIds(tableIds);
        if (tableIds.length === 0) {
          console.warn('RoleLayout: No tables assigned to server:', { userId, unitCode, unitType, companyCode });
          setNotifications([]);
          return;
        }

        const notificationsQuery = query(
          collection(db, 'notifications'),
          where('role', '==', 'servers'),
          where('unitCode', '==', unitCode),
          where('read', '==', false)
        );

        const unsubscribe = onSnapshot(notificationsQuery, (snapshot) => {
          const notificationList = snapshot.docs
            .map(doc => ({ id: doc.id, ...doc.data() }))
            .filter(notification => tableIds.includes(notification.tableId));
          setNotifications(notificationList);
          console.log(`RoleLayout: Fetched ${notificationList.length} notifications for servers`, { unitCode, unitType, companyCode, userId, assignedTableIds: tableIds, tabKey: location.key });
        }, (error) => {
          console.error('RoleLayout: Error fetching notifications for servers:', { unitCode, unitType, companyCode, userId, error });
          setError(`Failed to fetch notifications: ${error.message}`);
        });

        return () => unsubscribe();
      });
    } else if (roleLower === 'kitchen_staff' || roleLower === 'unit_manager') {
      const notificationsQuery = query(
        collection(db, 'notifications'),
        where('role', '==', roleLower),
        where('unitCode', '==', unitCode),
        where('read', '==', false),
        where('userId', '==', userId)
      );

      const unsubscribe = onSnapshot(notificationsQuery, (snapshot) => {
        const notificationList = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));
        setNotifications(notificationList);
        console.log(`RoleLayout: Fetched ${notificationList.length} notifications for ${roleLower}`, { unitCode, unitType, companyCode, userId, tabKey: location.key });
      }, (error) => {
        console.error(`RoleLayout: Error fetching notifications for ${roleLower}:`, { unitCode, unitType, companyCode, userId, error });
        setError(`Failed to fetch notifications: ${error.message}`);
      });

      return () => unsubscribe();
    } else {
      setNotifications([]);
    }
  }, [role, unitCode, userId, unitType, companyCode, location.key]);

  const handleLoginSuccess = (newRole, newUserId, { companyCode: newCompanyCode, companyName: newCompanyName, unitCode: newUnitCode, unitType: newUnitType, isPureVeg: newIsPureVeg }) => {
    onLoginSuccess(newRole, newUserId);
    if (newCompanyCode) {
      setCompanyCode(newCompanyCode);
      sessionStorage.setItem('companyCode', newCompanyCode);
      sessionStorage.setItem(`tab_${location.key}_companyCode`, newCompanyCode);
    }
    if (newUnitCode) {
      setUnitCode(newUnitCode);
      sessionStorage.setItem('unitCode', newUnitCode);
      sessionStorage.setItem(`tab_${location.key}_unitCode`, newUnitCode);
    }
    if (newCompanyName) {
      setCompanyName(newCompanyName);
      sessionStorage.setItem('companyName', newCompanyName);
      sessionStorage.setItem(`tab_${location.key}_companyName`, newCompanyName);
    }
    if (newUnitType) {
      setUnitType(newUnitType);
      sessionStorage.setItem('unitType', newUnitType);
      sessionStorage.setItem(`tab_${location.key}_unitType`, newUnitType);
    }
    if (newIsPureVeg !== null) {
      setIsPureVeg(newIsPureVeg);
      sessionStorage.setItem('isPureVeg', newIsPureVeg ? 'true' : 'false');
      sessionStorage.setItem(`tab_${location.key}_isPureVeg`, newIsPureVeg ? 'true' : 'false');
    }
    console.log('RoleLayout: handleLoginSuccess:', { newRole, newUserId, newCompanyCode, newCompanyName, newUnitCode, newUnitType, newIsPureVeg, tabKey: location.key });
  };

  const handleDrawerToggle = () => {
    setOpen(!open);
  };

  /*const toggleCartDrawer = () => {
    setOpenCartDrawer(!openCartDrawer);
  };*/

  const toggleCartDrawer = () => {
  setOpenCartDrawer(prev => !prev);
};

/*const toggleCartDrawer = (state) => {
  setOpenCartDrawer(state);
};*/
const userClosedCart = useRef(false);
const prevCartLength = useRef(0);
useEffect(() => {
  if (role?.toLowerCase() !== "customer") return;

  const isMenuPage = location.pathname.includes("/customer/menu");

  const addedNewItem = cart.length > prevCartLength.current;

  if (isMenuPage && addedNewItem && !userClosedCart.current) {
    setOpenCartDrawer(true);
  }

  prevCartLength.current = cart.length;
}, [cart.length, location.pathname]);

useEffect(() => {
  if (cart.length === 0) {
    userClosedCart.current = false;
  }
}, [cart.length]);

console.log("Parent openCartDrawer:", openCartDrawer);

const openCart = () => {
  console.log("OPEN CART CALLED"); 
  setOpenCartDrawer(true);
};

useEffect(() => {
  console.log("Parent openCartDrawer 2 :", openCartDrawer);
}, [openCartDrawer]);


const closeCart = () => setOpenCartDrawer(false);
  const handleLogout = async () => {
    localStorage.removeItem("userData");
    try {
      if (role.toLowerCase() !== 'customer') {
        await signOut(auth);
      }
      setSearchQuery('');
      setCart([]);
      localStorage.removeItem('cart');
      sessionStorage.removeItem(`tab_${location.key}_companyCode`);
      sessionStorage.removeItem(`tab_${location.key}_tableId`);
      sessionStorage.removeItem(`tab_${location.key}_sessionId`);
      sessionStorage.removeItem(`tab_${location.key}_unitCode`);
      sessionStorage.removeItem(`tab_${location.key}_unitType`);
      sessionStorage.removeItem(`tab_${location.key}_companyName`);
      sessionStorage.removeItem(`tab_${location.key}_isPureVeg`);
      sessionStorage.removeItem('companyCode');
      sessionStorage.removeItem('unitCode');
      sessionStorage.removeItem('unitType');
      sessionStorage.removeItem('companyName');
      sessionStorage.removeItem('isPureVeg');
      sessionStorage.removeItem('tableId');
      const queryParams = new URLSearchParams();
      if (tableId) queryParams.set('id', tableId);
      if (companyCode) queryParams.set('code', companyCode);
      if (unitCode) queryParams.set('unit', unitCode);
      if (unitType) queryParams.set('type', unitType);
      navigate(`/employee-login${queryParams.toString() ? `?${queryParams.toString()}` : ''}`);
    } catch (error) {
      console.error('RoleLayout: Error signing out:', { error, role, userId, unitCode, unitType, companyCode });
      setError(`Failed to log out: ${error.message}`);
    }
  };

  const clearCart = () => {
    setCart([]);
    localStorage.removeItem('cart');
  };

  const getRoleRoutes = () => {
    const roleLower = role.toLowerCase() || '';
    switch (roleLower) {
      case 'admin':
        return AdminRoutes;
      case 'customer':
        return CustomerRoutes;
      case 'kitchen_staff':
        return KitchenRoutes;
      case 'servers':
        return ServerRoutes;
      case 'softwareadmin':
        return MasterRoutes;
      case 'unit_manager':
        return UnitManagerRoutes;
      default:
        return CustomerRoutes;
    }
  };

  const defaultRoute = () => {
    const roleLower = role.toLowerCase();
    if (roleLower === 'admin') {
      return `/admin/home?code=${companyCode}&name=${encodeURIComponent(companyName || 'Unknown')}&isPureVeg=${isPureVeg || false}`;
    } else if (['unit_manager', 'kitchen_staff', 'servers'].includes(roleLower)) {
      return `/${roleLower}/home?unit=${unitCode}&code=${companyCode}&type=${unitType || 'Unknown'}&name=${encodeURIComponent(companyName || 'Unknown')}&isPureVeg=${isPureVeg || false}`;
    } /*else if (roleLower === 'customer') {
      return '/customer/home';
    }*/else if (roleLower === 'customer') {
  return '/customer/details';
}
    else {
      return '/';
    }
  };

  /*const isValidCustomerRoute = (path) => {
    if (role.toLowerCase() !== 'customer') return true;
    if (path === '/customer/home') return true;
    return companyCode && unitCode && tableId && unitType;
  };*/
  /*const isValidCustomerRoute = (path) => {
  if (role.toLowerCase() !== 'customer') return true;

  // ✅ Allow both pages
  if (path === '/customer/details' || path === '/customer/home') return true;

  return companyCode && unitCode && tableId && unitType;
};*/
const isValidCustomerRoute = (path) => {
  if (role.toLowerCase() !== 'customer') return true;

  // ✅ allow join-cart also
  if (
    path === '/customer/details' ||
    path === '/customer/home' ||
    path === '/customer/join-cart'
  ) return true;

  return companyCode && unitCode && tableId && unitType;
};

  const roleRoutes = getRoleRoutes();
  const sidebarWidthAdjustment = open ? 0 : drawerWidth;


  // All Listening Goes here.



  useEffect(() => {
    // 1. Start the subscription
    const unsubscribe = menuService.subscribeToMenuUpdates(() => {
      console.log("RoleLayout: Menu update received, refreshing menu data...");
    
    });
  
    // 2. Cleanup: This runs when the component is removed (navigated away)
    return () => {
      if (unsubscribe) unsubscribe();
    };


  }, []); // Empty dependency array means it runs once on mount
  
  

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        backgroundImage: darkMode
          ? 'url(https://static.vecteezy.com/ti/vetor-gratis/t2/21953476-desatado-rabisco-padronizar-velozes-comida-dentro-uma-plano-estilo-esboco-este-retrata-quente-cachorro-salsicha-pizza-hamburguer-frango-grade-sanduiche-frances-fritas-bebidas-salsicha-charme-em-uma-preto-fundo-gratis-vetor.jpg)'
          : 'url(https://png.pngtree.com/background/20210712/original/pngtree-seamless-food-pattern-in-doodle-style-and-blue-color-picture-image_1177117.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        overflowX: 'hidden',
      }}
    >
      {error && (
        <Box sx={{ p: 2, bgcolor: 'error.main', color: 'white', textAlign: 'center' }}>
          <Typography variant="body1">{error}</Typography>
        </Box>
      )}
      <Box sx={{ display: 'flex', flex: 1, overflowX: 'hidden' }}>
        <CssBaseline />
        <Navbar
          darkMode={darkMode}
          handleDrawerToggle={handleDrawerToggle}
          toggleDarkMode={toggleDarkMode}
          role={role}
          toggleCartDrawer={toggleCartDrawer}
          cartItemCount={Array.isArray(cart) ? cart.length : 0}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          userId={userId}
          handleLogout={handleLogout}
          notificationCount={notifications.length}
          notifications={notifications}
          companyName={companyName}
          billingPreference={billingPreference}
        />
        <Sidebar
          open={open}
          handleDrawerToggle={handleDrawerToggle}
          role={role}
          tableId={tableId}
          companyCode={companyCode}
          unitCode={unitCode}
          userId={userId}
          companyName={companyName}
          unitType={unitType}
          isPureVeg={isPureVeg}
          cartId={cartId}
          billingPreference={billingPreference}
        />
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 3,
            width: isMobile
              ? '100%'
              : `calc(100% - ${open ? drawerWidth : 0}px)`,
            marginRight: openCartDrawer && !isMobile ? `${drawerWidth}px` : 0,
            transition: theme.transitions.create(['width', 'margin'], {
              easing: theme.transitions.easing.easeInOut,
              duration: theme.transitions.duration.standard,
            }),
            bgcolor: darkMode ? 'rgba(0, 0, 0, 0.7)' : 'rgba(255, 255, 255, 0.9)',
            color: darkMode ? '#fff' : '#000',
            overflowX: 'hidden',
          }}
        >
          <DrawerHeader sx={{ height: '72px' }} />
          <Routes>
            <Route path="/" element={<LandingPage />} exact />
            {role && role.toLowerCase() === 'customer' ? (
              <>
                {roleRoutes.map((route) => (
                  <Route
                    key={route.path}
                    path={route.path}
                    element={
                      isValidCustomerRoute(route.path) ? (
                        React.cloneElement(route.element, { 
                          companyCode, 
                          companyName, 
                          unitType, 
                          tableId, 
                          unitCode, 
                          isPureVeg, 
                          cart, 
                          setCart, 
                          userId, 
                          darkMode 
                        })
                      ) : (
                        <Navigate to="/customer/home" replace />
                      )
                    }
                    exact={route.exact}
                  />
                ))}
                <Route
                  path="/customer/menu"
                  element={
                    isValidCustomerRoute('/customer/menu') ? (
                      <ViewMenu
                        cart={cart}
                        setCart={setCart}
                        searchQuery={searchQuery}
                        setSearchQuery={setSearchQuery}
                        companyCode={companyCode}
                        companyName={companyName}
                        unitType={unitType}
                        tableId={tableId}
                        unitCode={unitCode}
                        isPureVeg={isPureVeg}
                        darkMode={darkMode}
                        cartId={cartId}
                        serviceArea={serviceArea}  
                        updateCartInFirestore={updateCartInFirestore} 
                       openCartDrawer={openCartDrawer}
          toggleCartDrawer={toggleCartDrawer}
                      />
                    ) : (
                      <Navigate to="/customer/home" replace />
                    )
                  }
                  exact
                />
                <Route
                  path="/customer/order"
                  element={
                    isValidCustomerRoute('/customer/order') ? (
                      <PlaceOrder
                        setCart={setCart}
                        companyCode={companyCode}
                        companyName={companyName}
                        unitType={unitType}
                        tableId={tableId}
                        unitCode={unitCode}
                        isPureVeg={isPureVeg}
                        darkMode={darkMode}
                      />
                    ) : (
                      <Navigate to="/customer/home" replace />
                    )
                  }
                  exact
                />
                <Route
                  path="/customer/track-order"
                  element={
                    isValidCustomerRoute('/customer/track-order') ? (
                      <TrackOrder
                        companyCode={companyCode}
                        companyName={companyName}
                        unitType={unitType}
                        tableId={tableId}
                        unitCode={unitCode}
                        isPureVeg={isPureVeg}
                        darkMode={darkMode}
                      />
                    ) : (
                      <Navigate to="/customer/home" replace />
                    )
                  }
                  exact
                />
                <Route
                  path="/customer/bill"
                  element={
                    isValidCustomerRoute('/customer/bill') ? (
                      <RequestBill
                        companyCode={companyCode}
                        companyName={companyName}
                        unitType={unitType}
                        tableId={tableId}
                        unitCode={unitCode}
                        isPureVeg={isPureVeg}
                        darkMode={darkMode}
                        cartId={cartId}
                         serviceArea={serviceArea}  

                      />
                    ) : (
                      <Navigate to="/customer/home" replace />
                    )
                  }
                />
              </>
            ) : (
              <>
                {roleRoutes.map((route) => (
                  <Route
                    key={route.path}
                    path={route.path}
                    element={React.cloneElement(route.element, { 
                      companyCode, 
                      companyName, 
                      unitType, 
                      tableId, 
                      unitCode, 
                      isPureVeg, 
                      cart, 
                      setCart, 
                      userId, 
                      darkMode 
                    })}
                    exact={route.exact}
                  />
                ))}
                <Route path="*" element={<Navigate to={defaultRoute()} replace />} />
              </>
            )}
          </Routes>
        </Box>
      </Box>

      {role.toLowerCase() === 'customer' && (
        <Cart
          cart={cart}
          setCart={setCart}
          openCartDrawer={openCartDrawer}
          toggleCartDrawer={toggleCartDrawer}
          isMobile={isMobile}
          navigate={navigate}
          setCartMarginAdjustment={setCartMarginAdjustment}
          tableId={tableId}
          companyCode={companyCode}
          unitCode={unitCode}
          companyName={companyName}
          unitType={unitType}
          isPureVeg={isPureVeg}
          darkMode={darkMode}
          cartId={cartId}
          serviceArea={serviceArea}
          updateCartInFirestore={updateCartInFirestore} 
        />
      )}

      <Footer darkMode={darkMode} />
    </Box>
  );
}

export default RoleLayout;